import type { ReactNode } from 'react';
import { usePageTitlesVisible } from '@hooks/usePageTitlesVisible';
import { PageHeader } from '@components/ui/PageHeader/PageHeader';
import styles from './PageLayout.module.css';

interface IPageLayoutProps {
  /** The page's name — the sidebar's name for it. Absent on a page whose title is its content (a report, a guide). */
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Beside the title, e.g. a LIVE tag. */
  badge?: ReactNode;
  /** The page's own controls, right of the title. */
  actions?: ReactNode;
  /**
   * Full height, flush to the content area's edges: the body fills what the
   * header leaves and owns its own scrolling (a Gantt, a chat, a 3D viewer).
   * Without it the page is a padded column that scrolls with the app.
   */
  fill?: boolean;
  /**
   * A flow page with no gutters of its own: the content runs to the content
   * area's edges and brings its own (a report's full-width header strip). The
   * page still scrolls with the app. Ignored by `fill`, which is already flush.
   */
  bleed?: boolean;
  /** A `fill` page whose body keeps the page gutters (cards on the page colour) rather than running edge to edge. */
  inset?: boolean;
  children: ReactNode;
}

/**
 * The frame of every page inside the app shell: the gutters, the header (one
 * title look for every page, hidden by the user's "Page titles" setting) and
 * the space between it and the content. A page renders its content inside it
 * and never pads or positions itself against the shell.
 */
export function PageLayout({
  title,
  subtitle,
  badge,
  actions,
  fill = false,
  inset = false,
  bleed = false,
  children,
}: IPageLayoutProps) {
  const titlesVisible = usePageTitlesVisible();
  // The header takes space when it shows the title, or keeps controls on screen.
  const headShown = title !== undefined && (titlesVisible || actions !== undefined);

  const root = [styles.page, fill ? styles.fill : !bleed && styles.flow, fill && inset && styles.inset, headShown && styles.withHead]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={root}>
      {title !== undefined && (
        <PageHeader className={styles.head} title={title} subtitle={subtitle} badge={badge} actions={actions} />
      )}
      <div className={styles.body}>{children}</div>
    </div>
  );
}
